import React from "react";
import { useTranslation } from "react-i18next";
import { cn } from "../../../lib/utils";
import { Button, Input } from "../../ui";
import {
  ROOM_LOCATIONS,
  PURCHASE_YEARS,
  PRIMARY_CATEGORIES,
} from "../constants";
import type { WizardData } from "../hooks/useWizardState";
import { DEMO_FEATURES } from "../../../config/demo";

interface Props {
  data: WizardData;
  updateData: (patch: Partial<WizardData>) => void;
  onNext: () => void;
}

export function AssetDetails({ data, updateData, onNext }: Readonly<Props>) {
  const { t } = useTranslation('addAsset');
  const MONTHS = [
    { value: 1, label: t('details.months.1') },
    { value: 2, label: t('details.months.2') },
    { value: 3, label: t('details.months.3') },
    { value: 4, label: t('details.months.4') },
    { value: 5, label: t('details.months.5') },
    { value: 6, label: t('details.months.6') },
    { value: 7, label: t('details.months.7') },
    { value: 8, label: t('details.months.8') },
    { value: 9, label: t('details.months.9') },
    { value: 10, label: t('details.months.10') },
    { value: 11, label: t('details.months.11') },
    { value: 12, label: t('details.months.12') },
  ];
  // Auto-generate name preview
  const autoName = [data.brand, data.subcategoryLabel].filter(Boolean).join(" ");
  const displayName = data.name || autoName;

  // Get the category & subcategory icons for the summary card
  const category = PRIMARY_CATEGORIES.find((c) => c.id === data.primaryCategory);
  const _subcategory = category?.subcategories.find(
    (s) => s.id === data.subcategory
  );

  return (
    <div className="animate-fade-in-up">
      <div className="px-6 pb-3 pt-4">
        <h2 className="text-2xl font-light tracking-tight">
          {t('details.title')}
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          {t('details.subtitle')}
        </p>
      </div>

      <div className="px-6 pb-6 mt-3 space-y-5">
        {/* ── Summary Card ────────────────────────────────────────── */}
        <div className="p-5 rounded-2xl bg-primary/5 border-2 border-primary/20">
          <div className="grid grid-cols-2 gap-y-4 gap-x-4">
            <div>
              <p className="text-[11px] text-muted-foreground uppercase tracking-wider">
                {t('details.assetName')}
              </p>
              <p className="text-sm font-semibold mt-0.5 truncate">
                {displayName || "—"}
              </p>
            </div>
            <div>
              <p className="text-[11px] text-muted-foreground uppercase tracking-wider">
                {t('details.brand')}
              </p>
              <p className="text-sm font-semibold mt-0.5 truncate">
                {data.brand || "—"}
              </p>
            </div>
            <div>
              <p className="text-[11px] text-muted-foreground uppercase tracking-wider">
                {t('details.model')}
              </p>
              <p className="text-sm font-medium mt-0.5 truncate">
                {data.model || "—"}
              </p>
            </div>
            <div>
              <p className="text-[11px] text-muted-foreground uppercase tracking-wider">
                {t('details.serialNumber')}
              </p>
              <p className="text-sm font-medium mt-0.5 truncate">
                {data.serialNumber || "—"}
              </p>
            </div>
          </div>
        </div>

        {/* ── Custom Name (editable) ──────────────────────────────── */}
        <div>
          <label htmlFor="asset-name" className="text-sm font-medium mb-1.5 block">
            {t('details.assetName')}
          </label>
          <Input
            id="asset-name"
            value={data.name}
            onChange={(e) => updateData({ name: e.target.value })}
            placeholder={autoName || t('details.namePlaceholderFallback')}
            className="rounded-xl"
          />
          <p className="text-[11px] text-muted-foreground mt-1">
            {t('details.leaveBlankToUse', { name: autoName })}
          </p>
        </div>

        {/* ── Location (required) ─────────────────────────────────── */}
        <div>
          <p className="text-sm font-medium mb-2.5 block">
            {t('details.whereLocated')} <span className="text-destructive">*</span>
          </p>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {ROOM_LOCATIONS.map((room, idx) => {
              const Icon = room.icon;
              const isSelected = data.location === room.id;

              return (
                <button
                  key={room.id}
                  onClick={() => updateData({ location: room.id })}
                  style={{ animationDelay: `${idx * 0.04}s` }}
                  className={cn(
                    "animate-fade-in-up flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all cursor-pointer shadow-sm hover:shadow-md",
                    "hover:border-primary/50 hover:bg-primary/[0.03]",
                    isSelected
                      ? "border-primary bg-primary/5"
                      : "border-border bg-background"
                  )}
                >
                  <Icon
                    className={cn(
                      "h-5 w-5 transition-colors",
                      isSelected ? "text-primary" : "text-muted-foreground"
                    )}
                  />
                  <span className="text-[11px] font-medium leading-tight text-center">
                    {room.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Purchase Date (Year + Month) ─────────────────────── */}
        {DEMO_FEATURES.showHealthScore && (
          <div>
            <label className="text-sm font-medium mb-1.5 block">
              {t('details.purchaseDate')}{" "}
              <span className="text-muted-foreground text-xs font-normal">
                {t('details.optionalRecommended')}
              </span>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <select
                value={data.purchaseYear ?? ""}
                onChange={(e) =>
                  updateData({
                    purchaseYear: e.target.value ? Number.parseInt(e.target.value) : null,
                  })
                }
                className="w-full px-3 py-2 rounded-xl border bg-background text-sm focus:outline-none focus:ring-1 focus:ring-ring"
              >
                <option value="">{t('details.year')}</option>
                {PURCHASE_YEARS.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
              <select
                value={data.purchaseMonth ?? ""}
                onChange={(e) =>
                  updateData({
                    purchaseMonth: e.target.value ? Number.parseInt(e.target.value) : null,
                  })
                }
                className="w-full px-3 py-2 rounded-xl border bg-background text-sm focus:outline-none focus:ring-1 focus:ring-ring"
              >
                <option value="">{t('details.month')}</option>
                {MONTHS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* ── Purchase Price ────────────────────────────────────── */}
        {DEMO_FEATURES.showHealthScore && (
          <div>
            <label className="text-sm font-medium mb-1.5 block">
              {t('details.purchasePrice')}{" "}
              <span className="text-muted-foreground text-xs font-normal">
                {t('details.optional')}
              </span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                $
              </span>
              <Input
                type="number"
                min={0}
                step={0.01}
                value={data.purchasePrice ?? ""}
                onChange={(e) =>
                  updateData({
                    purchasePrice: e.target.value ? Number.parseFloat(e.target.value) : null,
                  })
                }
                placeholder="0.00"
                className="rounded-xl pl-7"
              />
            </div>
          </div>
        )}

        {/* ── Warranty Expiration ──────────────────────────────── */}
        {DEMO_FEATURES.showHealthScore && (
          <div>
            <label className="text-sm font-medium mb-1.5 block">
              {t('details.warrantyExpiration')}{" "}
              <span className="text-muted-foreground text-xs font-normal">
                {t('details.optional')}
              </span>
            </label>
            <Input
              type="date"
              value={data.warrantyExpiresAt}
              onChange={(e) => updateData({ warrantyExpiresAt: e.target.value })}
              className="rounded-xl"
            />
            <p className="text-[11px] text-muted-foreground mt-1">
              {t('details.warrantyHint')}
            </p>
          </div>
        )}

        {/* ── Continue ────────────────────────────────────────────── */}
        <Button
          onClick={onNext}
          disabled={!data.location}
          className="w-full rounded-xl"
        >
          {t('common.continue')}
        </Button>
      </div>
    </div>
  );
}
